import React, { useState, useCallback, useEffect, useRef } from "react";
import styles from "./RefluxMonitoring.module.css";

/**
 * Collapsible panel showing recent action dispatches.
 * Supports resizing via drag handle when expanded.
 * @param {Object} props
 * @param {Array} props.actions - Array of action log entries
 * @param {boolean} props.expanded - Whether panel is expanded
 * @param {Function} props.onToggle - Callback to toggle expanded state
 * @param {Function} props.onClear - Callback to clear action log
 * @param {Function} props.formatTime - Time formatting function
 * @param {number} props.width - Panel width in pixels
 * @param {Function} props.onWidthChange - Callback when width changes
 */
const ActionsPanel = ({
  actions,
  expanded,
  onToggle,
  onClear,
  formatTime,
  width,
  onWidthChange,
}) => {
  const panelRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);

  // Handle resize drag
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      if (!panelRef.current) return;
      const panelRect = panelRef.current.getBoundingClientRect();
      // Calculate new width from right edge minus mouse position
      const newWidth = panelRect.right - e.clientX;
      // Clamp between min and max
      const clampedWidth = Math.max(200, Math.min(600, newWidth));
      onWidthChange(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, onWidthChange]);

  // Collapsed state - vertical text strip
  if (!expanded) {
    return (
      <div
        className={styles.actionsPanelCollapsed}
        onClick={onToggle}
        onKeyDown={(e) => e.key === "Enter" && onToggle()}
        role="button"
        tabIndex={0}
        title="Click to expand actions panel"
      >
        <span className={styles.actionsPanelCollapsedText}>
          Actions ({actions.length})
        </span>
      </div>
    );
  }

  // Expanded state - full panel with resize handle
  return (
    <div
      ref={panelRef}
      className={styles.actionsPanelExpanded}
      style={{ width: `${width}px` }}
    >
      {/* Resize handle */}
      <div
        className={styles.actionsPanelResizeHandle}
        onMouseDown={handleMouseDown}
        title="Drag to resize"
      />

      {/* Header */}
      <div className={styles.actionsPanelHeader}>
        <span
          className={styles.actionsPanelTitle}
          onClick={onToggle}
          onKeyDown={(e) => e.key === "Enter" && onToggle()}
          role="button"
          tabIndex={0}
        >
          Actions ({actions.length})
        </span>
        <div className={styles.actionsPanelButtons}>
          {actions.length > 0 && (
            <button
              type="button"
              className={styles.actionsPanelClearBtn}
              onClick={onClear}
              title="Clear action log"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            className={styles.actionsPanelCollapseBtn}
            onClick={onToggle}
            title="Collapse panel"
          >
            ▶
          </button>
        </div>
      </div>

      {/* Action list */}
      <div className={styles.actionsPanelContent}>
        {actions.length === 0 ? (
          <div className={styles.emptyState}>No actions logged</div>
        ) : (
          <div className={styles.actionLog}>
            {[...actions].reverse().map((action, index) => (
              <div
                key={`${action.time}-${action.name}-${index}`}
                className={styles.actionRow}
              >
                <span className={styles.actionTime}>
                  {formatTime(action.time)}
                </span>
                <span className={styles.actionName}>{action.name}</span>
                {action.source && (
                  <span className={styles.actionSource}>{action.source}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionsPanel;
